import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { productCategoriesTabsState, productsDataState } from "../../Recoil/atoms";
import HeroBanner from "../../components/HeroBanner";
import SingularTabs from "../../components/SingularTabs";
import { callApi } from "../../api/callApi";
import AppData from "../../AppData";

const Default = () => {
  const [tabs, setTabs] = useRecoilState(productCategoriesTabsState);
  const [productsData, setProductsData] = useRecoilState(productsDataState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategoriesAndProducts();
  }, []);

  const fetchCategoriesAndProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch categories
      callApi({
        url: AppData.BASE_URL + 'api/v1/categories',
        method: 'GET',
        callback: (categoriesResponse) => {
          console.log('Categories Response:', categoriesResponse);
          if (categoriesResponse && categoriesResponse.data) {
            // Handle different response structures
            let categoriesData = categoriesResponse.data;
            
            // If data is wrapped in another property, extract it
            if (categoriesData.categories) {
              categoriesData = categoriesData.categories;
            } else if (categoriesData.data) {
              categoriesData = categoriesData.data;
            }
            
            // Ensure we have an array
            if (Array.isArray(categoriesData)) {
              const formattedTabs = categoriesData.map((category, index) => ({
                id: category.id || index,
                name: category.name,
                active: index === 0
              }));
              setTabs(formattedTabs);
            } else {
              console.error('Categories data is not an array:', categoriesData);
              setError('Invalid categories data format');
            }
          } else {
            console.error('No categories data received');
            setError('No categories data received');
          }
        }
      });
      
      // Fetch products
      callApi({
        url: AppData.BASE_URL + 'api/v1/products',
        method: 'GET',
        callback: (productsResponse) => {
          console.log('Products Response:', productsResponse);
          if (productsResponse && productsResponse.data) {
            // Handle different response structures
            let productsData = productsResponse.data;
            
            // If data is wrapped in another property, extract it
            if (productsData.products) {
              productsData = productsData.products;
            } else if (productsData.data) {
              productsData = productsData.data;
            }
            
            // Ensure we have an array
            if (Array.isArray(productsData)) {
              // Group products by category
              const groupedProducts = {};
              productsData.forEach(product => {
                // Handle different category data types
                let categoryName = 'uncategorized';
                
                if (product.category) {
                  if (typeof product.category === 'string') {
                    categoryName = product.category.toLowerCase();
                  } else if (typeof product.category === 'object' && product.category.name) {
                    categoryName = product.category.name.toLowerCase();
                  } else if (typeof product.category === 'object' && product.category.title) {
                    categoryName = product.category.title.toLowerCase();
                  } else {
                    // If category is a number or other type, convert to string
                    categoryName = String(product.category).toLowerCase();
                  }
                }
                
                if (!groupedProducts[categoryName]) {
                  groupedProducts[categoryName] = [];
                }
                groupedProducts[categoryName].push(product);
              });
              setProductsData(groupedProducts);
            } else {
              console.error('Products data is not an array:', productsData);
              setError('Invalid products data format');
            }
          } else {
            console.error('No products data received');
            setError('No products data received');
          }
          setLoading(false);
        }
      });
    } catch (err) {
      setError('Failed to load categories and products');
      setLoading(false);
    }
  };

  const getActiveTabProducts = () => {
    const activeTab = tabs.find(tab => tab.active);
    if (!activeTab) return [];
    
    const categoryName = activeTab.name.toLowerCase();
    return productsData[categoryName] || [];
  };

  const truncateDescription = (text, maxLines = 5) => {
    if (!text) return '';
    const words = text.split(' ');
    const wordsPerLine = 8; // Approximate words per line
    const maxWords = maxLines * wordsPerLine;
    
    if (words.length <= maxWords) {
      return text;
    }
    
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const openModal = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setIsModalOpen(false);
  };

  const renderProductItem = (product, key) => {
    const truncatedDescription = truncateDescription(product.description);
    const needsReadMore = product.description && product.description.length > truncatedDescription.length;
    
    return (
      <div className="product-item" key={key}>
        <div className="product-image">
          <img src={product.image} alt={product.name} />
        </div>
        <div className="product-details">
          <h3 className="product-name font14 bold-text">{product.name}</h3>
          <p className="product-description font12">{truncatedDescription}</p>
          {needsReadMore && (
            <button 
              className="read-more-btn font12"
              onClick={() => openModal(product)}
            >
              Read More
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (!isModalOpen || !selectedProduct) return null;

    return (
      <div className="modal-overlay" onClick={closeModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="font16 bold-text">{selectedProduct.name}</h2>
            <button className="modal-close" onClick={closeModal}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="modal-image">
              <img src={selectedProduct.image} alt={selectedProduct.name} />
            </div>
            <div className="modal-description">
              <p className="font14">{selectedProduct.description}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const activeProducts = getActiveTabProducts();

  return (
    <div>
      <HeroBanner name="Products" />
      {renderModal()}

      <div className="rest-page content-wrapper" style={{ minHeight: "30vh" }}>
        <SingularTabs tabs={tabs} setTabsState={setTabs} />

        <div className="products-container">
          {loading ? (
            <div className="loading-container">
              <p className="font14">Loading products...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="font14 error-text">{error}</p>
              <button 
                className="button" 
                onClick={fetchCategoriesAndProducts}
                style={{ marginTop: '10px' }}
              >
                Retry
              </button>
            </div>
          ) : activeProducts && activeProducts.length > 0 ? (
            <div className="products-grid">
              {activeProducts.map((product, key) => {
                return renderProductItem(product, key);
              })}
            </div>
          ) : (
            <div className="no-products">
              <p className="font14">No products available in this category.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Default;
